import { useState, useEffect } from "react";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Icon from "@mui/material/Icon";
import TextField from "@mui/material/TextField";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import MenuItem from "@mui/material/MenuItem";
import Chip from "@mui/material/Chip";
import Tooltip from "@mui/material/Tooltip";
import axios from "axios";
import API_ENDPOINTS from "config/api";
import { QRCode } from "react-qr-code";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import DataTable from "examples/Tables/DataTable";
import QRScanner from "./QRScanner";

export default function ProductosInventario() {
  const [productos, setProductos] = useState([]);
  const [openDialog, setOpenDialog] = useState(false);
  const [openQRDialog, setOpenQRDialog] = useState(false);
  const [openQRScanner, setOpenQRScanner] = useState(false);
  const [selectedQRCode, setSelectedQRCode] = useState(null);
  const [categorias, setCategorias] = useState([]);
  const [almacenes, setAlmacenes] = useState([]);
  const [unidades, setUnidades] = useState(["UND", "KG", "LT", "M", "M2", "M3", "CJ", "PQ"]);
  const [searchTerm, setSearchTerm] = useState("");
  const [productoForm, setProductoForm] = useState({
    id: null,
    codigo: "",
    nombre: "",
    descripcion: "",
    categoria_id: "",
    precio_compra: "",
    precio_venta: "",
    stock_actual: "",
    stock_minimo: "",
    stock_maximo: "",
    unidad_medida: "UND",
    almacen_id: "",
    estado: "activo",
  });

  const empresaId = localStorage.getItem("empresaId");

  useEffect(() => {
    fetchProductos();
    fetchCategorias();
    fetchAlmacenes();
  }, [empresaId]);

  const fetchProductos = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.INVENTARIO.GENERAL, {
        params: { empresa_id: empresaId },
      });
      setProductos(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error al obtener productos:", error);
      setProductos([]);
    }
  };

  const fetchCategorias = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.CATEGORIAS, {
        params: { empresa_id: empresaId },
      });
      setCategorias(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error al obtener categorías:", error);
      setCategorias([]);
    }
  };

  const fetchAlmacenes = async () => {
    try {
      const response = await axios.get(API_ENDPOINTS.ALMACENES, {
        params: { empresa_id: empresaId },
      });
      setAlmacenes(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error("Error al obtener almacenes:", error);
      setAlmacenes([]);
    }
  };

  const handleOpenDialog = (producto = null) => {
    if (producto) {
      setProductoForm(producto);
    } else {
      setProductoForm({
        id: null,
        codigo: "",
        nombre: "",
        descripcion: "",
        categoria_id: "",
        precio_compra: "",
        precio_venta: "",
        stock_actual: "",
        stock_minimo: "",
        stock_maximo: "",
        unidad_medida: "UND",
        almacen_id: "",
        estado: "activo",
      });
    }
    setOpenDialog(true);
  };

  const handleCloseDialog = () => {
    setOpenDialog(false);
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setProductoForm({ ...productoForm, [name]: value });
  };

  const handleSubmit = async () => {
    try {
      const data = { ...productoForm, empresa_id: empresaId };
      if (productoForm.id) {
        await axios.put(API_ENDPOINTS.INVENTARIO.ITEM(productoForm.id), data);
      } else {
        await axios.post(API_ENDPOINTS.INVENTARIO.GENERAL, data);
      }
      fetchProductos();
      handleCloseDialog();
    } catch (error) {
      console.error("Error al guardar producto:", error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm("¿Está seguro de eliminar este producto?")) {
      try {
        await axios.delete(API_ENDPOINTS.INVENTARIO.ITEM(id));
        fetchProductos();
      } catch (error) {
        console.error("Error al eliminar producto:", error);
      }
    }
  };

  const handleOpenQRDialog = (codigo) => {
    setSelectedQRCode(codigo);
    setOpenQRDialog(true);
  };

  const handleCloseQRDialog = () => {
    setOpenQRDialog(false);
    setSelectedQRCode(null);
  };

  const handleOpenScanner = () => {
    setOpenQRScanner(true);
  };

  const handleCloseScanner = () => {
    setOpenQRScanner(false);
  };

  const handleQRScan = (decodedText) => {
    setSearchTerm(decodedText);
    // Buscar el producto automáticamente
    const producto = productos.find(
      (p) => p.codigo === decodedText || p.id.toString() === decodedText
    );
    if (producto) {
      handleOpenDialog(producto);
    }
  };

  const downloadQR = () => {
    const svg = document.getElementById("qr-code-svg");
    const svgData = new XMLSerializer().serializeToString(svg);
    const canvas = document.createElement("canvas");
    const ctx = canvas.getContext("2d");
    const img = new Image();
    img.onload = () => {
      canvas.width = img.width;
      canvas.height = img.height;
      ctx.drawImage(img, 0, 0);
      const pngFile = canvas.toDataURL("image/png");
      const downloadLink = document.createElement("a");
      downloadLink.download = `QR-${selectedQRCode}`;
      downloadLink.href = pngFile;
      downloadLink.click();
    };
    img.src = "data:image/svg+xml;base64," + btoa(svgData);
  };

  const getStockStatus = (producto) => {
    if (producto.stock_actual <= producto.stock_minimo) {
      return <Chip label="Stock Bajo" color="error" size="small" />;
    } else if (producto.stock_actual >= producto.stock_maximo) {
      return <Chip label="Stock Alto" color="warning" size="small" />;
    }
    return <Chip label="Normal" color="success" size="small" />;
  };

  const filteredProductos = productos.filter(
    (producto) =>
      producto.nombre.toLowerCase().includes(searchTerm.toLowerCase()) ||
      producto.codigo.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const columns = [
    { Header: "Código", accessor: "codigo", width: "10%", align: "left" },
    { Header: "Nombre", accessor: "nombre", align: "left" },
    { Header: "Categoría", accessor: "categoria", width: "15%", align: "left" },
    { Header: "Stock", accessor: "stock", width: "10%", align: "center" },
    { Header: "Estado", accessor: "estado", width: "10%", align: "center" },
    { Header: "Precio Venta", accessor: "precio", width: "12%", align: "right" },
    { Header: "Acciones", accessor: "acciones", width: "10%", align: "center" },
  ];

  const rows = filteredProductos.map((producto) => ({
    codigo: producto.codigo,
    nombre: producto.nombre,
    categoria: producto.categoria?.nombre || "Sin categoría",
    stock: `${producto.stock_actual} ${producto.unidad_medida}`,
    estado: getStockStatus(producto),
    precio: `$${parseFloat(producto.precio_venta).toLocaleString()}`,
    acciones: (
      <MDBox display="flex" gap={1} justifyContent="center">
        <Tooltip title="Ver QR">
          <IconButton
            size="small"
            color="secondary"
            onClick={() => handleOpenQRDialog(producto.codigo)}
          >
            <Icon>qr_code</Icon>
          </IconButton>
        </Tooltip>
        <IconButton size="small" color="info" onClick={() => handleOpenDialog(producto)}>
          <Icon>edit</Icon>
        </IconButton>
        <IconButton size="small" color="error" onClick={() => handleDelete(producto.id)}>
          <Icon>delete</Icon>
        </IconButton>
      </MDBox>
    ),
  }));

  return (
    <MDBox>
      <MDBox display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <MDTypography variant="h6">Gestión de Productos</MDTypography>
        <MDBox display="flex" gap={1}>
          <MDButton
            variant="outlined"
            color="info"
            startIcon={<Icon>qr_code_scanner</Icon>}
            onClick={handleOpenScanner}
          >
            Escanear QR
          </MDButton>
          <MDButton
            variant="gradient"
            color="success"
            startIcon={<Icon>add</Icon>}
            onClick={() => handleOpenDialog()}
          >
            Nuevo Producto
          </MDButton>
        </MDBox>
      </MDBox>

      <Card>
        <MDBox p={3}>
          <TextField
            fullWidth
            label="Buscar producto por nombre o código"
            variant="outlined"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            InputProps={{
              startAdornment: <Icon>search</Icon>,
            }}
          />
        </MDBox>
        <MDBox>
          <DataTable
            table={{ columns, rows }}
            isSorted={false}
            entriesPerPage={false}
            showTotalEntries={false}
            noEndBorder
          />
        </MDBox>
      </Card>

      {/* Dialog para crear/editar producto */}
      <Dialog
        open={openDialog}
        onClose={handleCloseDialog}
        maxWidth="md"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            overflow: "visible",
          },
        }}
      >
        <DialogTitle sx={{ pb: 2, pt: 2.5 }}>
          <MDBox display="flex" justifyContent="space-between" alignItems="center">
            <MDTypography variant="h5" fontWeight="medium">
              {productoForm.id ? "Editar Producto" : "Nuevo Producto"}
            </MDTypography>
            <IconButton onClick={handleCloseDialog} size="small">
              <Icon>close</Icon>
            </IconButton>
          </MDBox>
        </DialogTitle>
        <DialogContent sx={{ pt: 2, pb: 3, overflow: "visible" }}>
          <Grid container spacing={2.5}>
            {/* Código y Nombre */}
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Código"
                name="codigo"
                value={productoForm.codigo}
                onChange={handleInputChange}
                required
                variant="outlined"
                sx={{
                  "& .MuiInputBase-root": {
                    minHeight: "45px",
                  },
                  "& .MuiInputBase-input": {
                    cursor: "text",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={8}>
              <TextField
                fullWidth
                label="Nombre del Producto"
                name="nombre"
                value={productoForm.nombre}
                onChange={handleInputChange}
                required
                variant="outlined"
                sx={{
                  "& .MuiInputBase-root": {
                    minHeight: "45px",
                  },
                  "& .MuiInputBase-input": {
                    cursor: "text",
                  },
                }}
              />
            </Grid>

            {/* Descripción */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Descripción"
                name="descripcion"
                value={productoForm.descripcion}
                onChange={handleInputChange}
                multiline
                rows={3}
                variant="outlined"
                sx={{
                  "& .MuiInputBase-input": {
                    cursor: "text",
                  },
                }}
              />
            </Grid>

            {/* Categoría y Almacén */}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Categoría"
                name="categoria_id"
                value={productoForm.categoria_id || ""}
                onChange={handleInputChange}
                variant="outlined"
                SelectProps={{
                  displayEmpty: true,
                }}
                InputLabelProps={{
                  shrink: true,
                }}
                sx={{
                  "& .MuiInputBase-root": {
                    minHeight: "45px",
                    cursor: "pointer",
                  },
                  "& .MuiSelect-select": {
                    cursor: "pointer",
                    paddingTop: "8px",
                    paddingBottom: "8px",
                  },
                  "& .MuiInputLabel-root": {
                    backgroundColor: "white",
                    paddingX: 0.5,
                  },
                }}
              >
                <MenuItem value="">
                  <em>Ninguna</em>
                </MenuItem>
                {categorias.map((cat) => (
                  <MenuItem key={cat.id} value={cat.id}>
                    {cat.nombre}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                select
                label="Almacén"
                name="almacen_id"
                value={productoForm.almacen_id || ""}
                onChange={handleInputChange}
                variant="outlined"
                SelectProps={{
                  displayEmpty: true,
                }}
                InputLabelProps={{
                  shrink: true,
                }}
                sx={{
                  "& .MuiInputBase-root": {
                    minHeight: "45px",
                    cursor: "pointer",
                  },
                  "& .MuiSelect-select": {
                    cursor: "pointer",
                    paddingTop: "8px",
                    paddingBottom: "8px",
                  },
                  "& .MuiInputLabel-root": {
                    backgroundColor: "white",
                    paddingX: 0.5,
                  },
                }}
              >
                <MenuItem value="">
                  <em>Ninguno</em>
                </MenuItem>
                {almacenes.map((alm) => (
                  <MenuItem key={alm.id} value={alm.id}>
                    {alm.nombre}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Precios y Unidad */}
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Precio de Compra"
                name="precio_compra"
                type="number"
                value={productoForm.precio_compra || ""}
                onChange={handleInputChange}
                variant="outlined"
                inputProps={{ step: "0.01", min: "0" }}
                sx={{
                  "& .MuiInputBase-root": {
                    minHeight: "45px",
                  },
                  "& .MuiInputBase-input": {
                    cursor: "text",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Precio de Venta"
                name="precio_venta"
                type="number"
                value={productoForm.precio_venta || ""}
                onChange={handleInputChange}
                variant="outlined"
                inputProps={{ step: "0.01", min: "0" }}
                sx={{
                  "& .MuiInputBase-root": {
                    minHeight: "45px",
                  },
                  "& .MuiInputBase-input": {
                    cursor: "text",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                select
                label="Unidad de Medida"
                name="unidad_medida"
                value={productoForm.unidad_medida || "UND"}
                onChange={handleInputChange}
                variant="outlined"
                InputLabelProps={{
                  shrink: true,
                }}
                sx={{
                  "& .MuiInputBase-root": {
                    minHeight: "45px",
                    cursor: "pointer",
                  },
                  "& .MuiSelect-select": {
                    cursor: "pointer",
                    paddingTop: "8px",
                    paddingBottom: "8px",
                  },
                  "& .MuiInputLabel-root": {
                    backgroundColor: "white",
                    paddingX: 0.5,
                  },
                }}
              >
                {unidades.map((unidad) => (
                  <MenuItem key={unidad} value={unidad}>
                    {unidad}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            {/* Stock */}
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Stock Actual"
                name="stock_actual"
                type="number"
                value={productoForm.stock_actual || ""}
                onChange={handleInputChange}
                variant="outlined"
                inputProps={{ step: "1", min: "0" }}
                sx={{
                  "& .MuiInputBase-root": {
                    minHeight: "45px",
                  },
                  "& .MuiInputBase-input": {
                    cursor: "text",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Stock Mínimo"
                name="stock_minimo"
                type="number"
                value={productoForm.stock_minimo || ""}
                onChange={handleInputChange}
                variant="outlined"
                inputProps={{ step: "1", min: "0" }}
                sx={{
                  "& .MuiInputBase-root": {
                    minHeight: "45px",
                  },
                  "& .MuiInputBase-input": {
                    cursor: "text",
                  },
                }}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <TextField
                fullWidth
                label="Stock Máximo"
                name="stock_maximo"
                type="number"
                value={productoForm.stock_maximo || ""}
                onChange={handleInputChange}
                variant="outlined"
                inputProps={{ step: "1", min: "0" }}
                sx={{
                  "& .MuiInputBase-root": {
                    minHeight: "45px",
                  },
                  "& .MuiInputBase-input": {
                    cursor: "text",
                  },
                }}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5, pt: 1.5 }}>
          <MDButton variant="outlined" color="secondary" onClick={handleCloseDialog}>
            Cancelar
          </MDButton>
          <MDButton
            variant="gradient"
            color={productoForm.id ? "info" : "success"}
            onClick={handleSubmit}
          >
            {productoForm.id ? "Guardar Cambios" : "Crear Producto"}
          </MDButton>
        </DialogActions>
      </Dialog>

      {/* Dialog para mostrar QR */}
      <Dialog open={openQRDialog} onClose={handleCloseQRDialog} maxWidth="sm">
        <DialogTitle>
          <MDBox display="flex" justifyContent="space-between" alignItems="center">
            <MDTypography variant="h5">Código QR del Producto</MDTypography>
            <IconButton onClick={handleCloseQRDialog} size="small">
              <Icon>close</Icon>
            </IconButton>
          </MDBox>
        </DialogTitle>
        <DialogContent>
          <MDBox display="flex" flexDirection="column" alignItems="center" py={3}>
            {selectedQRCode && (
              <>
                <QRCode
                  id="qr-code-svg"
                  value={selectedQRCode}
                  size={256}
                  level="H"
                  includeMargin={true}
                />
                <MDTypography variant="caption" color="text" mt={2}>
                  Código: {selectedQRCode}
                </MDTypography>
              </>
            )}
          </MDBox>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={downloadQR}
            variant="contained"
            color="primary"
            startIcon={<Icon>download</Icon>}
          >
            Descargar QR
          </Button>
          <Button onClick={handleCloseQRDialog}>Cerrar</Button>
        </DialogActions>
      </Dialog>

      {/* Scanner QR */}
      <QRScanner open={openQRScanner} onClose={handleCloseScanner} onScan={handleQRScan} />
    </MDBox>
  );
}
